import { KeyboardReturn, Search } from '@mui/icons-material'
import { IconButton, InputAdornment, TextField } from '@mui/material'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

interface SearchInputProps {
    value: string
    onChange: (value: string) => void
    onSubmit: (e: React.KeyboardEvent | React.MouseEvent) => void
    onArrowUp?: () => void
    onArrowDown?: () => void
    onArrowLeft?: () => void
    onArrowRight?: () => void
    hasSelection?: boolean
    onInputFocus?: () => void
}

export interface SearchInputHandle {
    focus: () => void
}

export const SearchInput = forwardRef<SearchInputHandle, SearchInputProps>(
    (
        {
            value,
            onChange,
            onSubmit,
            onArrowUp,
            onArrowDown,
            onArrowLeft,
            onArrowRight,
            hasSelection = false,
            onInputFocus,
        },
        ref
    ) => {
        const inputRef = useRef<HTMLInputElement>(null)

        useImperativeHandle(ref, () => ({
            focus: () => inputRef.current?.focus(),
        }))

        useEffect(() => {
            inputRef.current?.focus()
        }, [])

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault()
                onSubmit(e)
            } else if (e.key === 'ArrowDown') {
                e.preventDefault()
                onArrowDown?.()
            } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                onArrowUp?.()
            } else if (e.key === 'ArrowLeft' && hasSelection) {
                e.preventDefault()
                onArrowLeft?.()
            } else if (e.key === 'ArrowRight' && hasSelection) {
                e.preventDefault()
                onArrowRight?.()
            }
        }

        return (
            <TextField
                inputRef={inputRef}
                fullWidth
                placeholder="Search..."
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onClick={onInputFocus}
                variant="outlined"
                size="small"
                autoComplete="off"
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                    ),
                    endAdornment: value.trim() && (
                        <InputAdornment position="end">
                            <IconButton
                                size="small"
                                onClick={(e) => onSubmit(e)}
                                edge="end"
                                sx={{
                                    color: 'text.secondary',
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                    gap: 0.5,
                                }}
                            >
                                Enter
                                <KeyboardReturn sx={{ fontSize: 14 }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: hasSelection
                            ? 'transparent'
                            : 'other.background',
                        '& fieldset': {
                            borderColor: hasSelection
                                ? 'divider'
                                : 'primary.main',
                        },
                    },
                }}
            />
        )
    }
)
